import { KemuServiceProduct, LimitedOrganizationInfo, SubscriptionInfo } from '@kemu-io/kemu-types';
import { clerkApiInterceptor } from '../authenticatedApi/clerkApiInterceptor';
import globals from '../../common/globals';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';

const PLATFORM_API = globals.PLATFORM_API_NAME;
const returnAxiosResponse = { response: true };

export type ValidStripeAddress = StripeAddressElementChangeEvent['value']['address'];


// Types
export type CreateCheckoutIntentRequest = {
	/** Selected price to purchase */
	priceId: string;
	/** Quantity to purchase (usually 1) */
	quantity?: number;
};

export type PriceInfo = {
  id: string;
  currency: string;
  unitAmount: number;
  recurringInterval?: string;
  recurringIntervalCount?: number;
  productName: string;
  productDescription?: string;
}


export type InitCheckoutIntentResponse = {
  priceInfo: PriceInfo;
  currentPlanInfo: SubscriptionInfo;
  paymentMethods: {
		isDefaultMethod: boolean;
    address: ValidStripeAddress;
		id: string;
		brand: string;
		last4: string;
		expMonth: number;
		expYear: number;
	}[];
  clientSecret?: string;
};

export type CalculateTaxesResponse = {
  amountTotal: number;
  taxAmount: number;
  currency: string;
  taxDetails: {
    country: string;
    percentage: string;
    type: string;
    reason: string;
  }[];
}

export type GetTiersResponse = {
	tiers: KemuServiceProduct[];
};


/**
 * Fetch available service tiers
 * Uses dummy data for MVP with simulated latency
 */
const getTiers = async (): Promise<GetTiersResponse> => {
  const response = await clerkApiInterceptor.get(PLATFORM_API, '/v1/checkout/products', {
		...returnAxiosResponse,
	});

	return response.data as GetTiersResponse;
};

/**
 * Creates a checkout intent on the server and returns the Stripe client secret
 * Placeholder implementation; wire to backend endpoint when available
 */
const createCheckoutIntent = async (payload: CreateCheckoutIntentRequest): Promise<InitCheckoutIntentResponse> => {
	const response = await clerkApiInterceptor.post(PLATFORM_API, `/v1/checkout/intent`, { body: payload, response: true });
	return response.data as InitCheckoutIntentResponse;
};


/**
 * POST /checkout/taxes
 * Calculates the taxes for a given amount and address
 */
const calculateTaxes = async (
	amount: number,
	address: ValidStripeAddress,
	currency: string
): Promise<CalculateTaxesResponse> => {
	const response = await clerkApiInterceptor.post(PLATFORM_API, '/v1/checkout/calculate-taxes', {
		...returnAxiosResponse,
		body: {
			amount,
			address,
			currency
		}
	});

	return response.data as CalculateTaxesResponse;
};

export type ChangeTierRequest = {
  /** the target price ID */
  priceId: string;
  /** the id of the payment method to use for the subscription */
  paymentMethodId: string;
  /** the id of the other non-tier prices to keep */
  nonTierPricesIdsToKeep?: string[];
  /** the id of the other non-tier prices to add */
  nonTierPricesToAdd?: {
    priceId: string;
    quantity?: number;
  }[];
};

export type SubscribeToTierResponse = {
	organization: LimitedOrganizationInfo;
};

export type DeletePaymentMethodResponse = {
	paymentMethods: {
		isDefaultMethod: boolean;
		address: ValidStripeAddress;
		id: string;
		brand: string;
		last4: string;
		expMonth: number;
		expYear: number;
	}[];
};

/**
 * POST /checkout/change-tier
 * Subscribes user to a new tier using an existing or new payment method
 */
const subscribeToTier = async (config: ChangeTierRequest): Promise<SubscribeToTierResponse> => {
	const response = await clerkApiInterceptor.post(PLATFORM_API, '/v1/checkout/change-tier', {
		...returnAxiosResponse,
		body: {
			...config,
		}
	});

	return response.data as SubscribeToTierResponse;
};

/**
 * DELETE /checkout/payment-method/{paymentMethodId}
 * Deletes a payment method and returns the updated list of payment methods
 */
const deletePaymentMethod = async (paymentMethodId: string): Promise<DeletePaymentMethodResponse> => {
	const response = await clerkApiInterceptor.delete(PLATFORM_API, `/v1/checkout/payment-method/${paymentMethodId}`, {
		...returnAxiosResponse,
	});

	return response.data as DeletePaymentMethodResponse;
};

const checkoutApi = {
	getTiers,
	createCheckoutIntent,
	calculateTaxes,
	subscribeToTier,
	deletePaymentMethod,
};

export default checkoutApi;